import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ChartDataSets, ChartOptions, ChartType } from 'chart.js';
import { AffiliateLinkActivityDto } from 'src/app/core/dtos/dto';
import { AccountService } from 'src/app/core/services/account.service';
import { AffiliateLinkSummaryDto, CurrencyDetailDto } from 'src/app/core/dtos/affiliate-data';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { HotToastService } from '@ngneat/hot-toast';

@Component({
  selector: 'app-affiliate-dashboard',
  templateUrl: './affiliate-dashboard.component.html',
  styleUrls: ['./affiliate-dashboard.component.scss']
})
export class AffiliateDashboardComponent implements OnInit {

  barChartOptions: ChartOptions = {
    responsive: true,
    scales: {
      yAxes: [
        { type: "linear", id: "y-axis-1", display: true, position: "left", ticks: { min: 0, stepSize: 20 }, scaleLabel: { display: true, labelString: '# Count' } },
        { type: "linear", id: "y-axis-2", display: true, position: "right", ticks: { min: 0, stepSize: 200 }, scaleLabel: { display: true, labelString: '$ Amount' } }
      ]
    }
  };
  barChartType: ChartType = 'bar';
  barChartLegend = true;
  barChartData: ChartDataSets[] = [
    { data: [], label: 'Clicks', stack: 'a', yAxisID: 'y-axis-1' },
    { data: [], label: 'Accounts created', stack: 'b', yAxisID: 'y-axis-1' },
    { data: [], label: '# of Purchases', stack: 'c', yAxisID: 'y-axis-1' },
    { data: [], label: '$ of Purchases', type: 'line', yAxisID: 'y-axis-2' },
  ];
  barChartLabels: string[] = ["Jan-Mar", "Apr-Jun", "July-Sep", "Oct-Dec"];

  summaryData: AffiliateLinkSummaryDto[] = [];
  affiliateLinkSummaries: AffiliateLinkSummaryDto[] = [];
  clickActivitiesList: AffiliateLinkActivityDto[] = [];
  purchaseActivitesList: AffiliateLinkActivityDto[] = [];
  createActivitiesList: AffiliateLinkActivityDto[] = [];

  constructor(
    private accountService: AccountService,
    private toastService: HotToastService
  ) { }

  ngOnInit(): void {
    this.populateAffiliateSummary();
  }

  populateAffiliateSummary() {
    this.accountService.getAffiliateSummary().subscribe(
      result => {
        result.content.forEach(data => {
          data.clickActivities.forEach(clickActivity => {
            this.clickActivitiesList.push(clickActivity);
          });
          data.purchaseActivities.forEach(purchaseActivity => {
            this.purchaseActivitesList.push(purchaseActivity);
          });
          data.createAccountActivities.forEach(createAccountActivity => {
            this.createActivitiesList.push(createAccountActivity);
          });
        });
        const clickActivities = this.clickActivitiesList.filter((a) => (new Date(a.dateCreatedUtc)).getFullYear() === (new Date()).getFullYear());
        const createAccountActivities = this.createActivitiesList.filter((a) => (new Date(a.dateCreatedUtc)).getFullYear() === (new Date()).getFullYear());
        const purchaseActivities = this.purchaseActivitesList.filter((a) => (new Date(a.dateCreatedUtc)).getFullYear() === (new Date()).getFullYear());

        this.barChartData[0].data = this.generateActivityCountsForQuarter(clickActivities);
        this.barChartData[1].data = this.generateActivityCountsForQuarter(createAccountActivities);
        this.barChartData[2].data = this.generateActivityCountsForQuarter(purchaseActivities);
        this.barChartData[3].data = this.generatePurchaseAmountsForQuarter(purchaseActivities);

        let totalAmount = 0;
        purchaseActivities.forEach(purchase => {
          totalAmount += purchase.purchaseAmount;
        });
        result.content.forEach((affiliateLink => {
          const listCurrencyDetail: CurrencyDetailDto[] = [];
          let currency: string;
          affiliateLink.currencyIds.forEach(currencyId => {
            switch (currencyId) {
              case 1: currency = 'CAD'; break;
              case 2: currency = 'USD'; break;
              case 3: currency = 'CNY'; break;
              case 4: currency = 'INR'; break;
              default: currency = 'NA';
            }
            const currencyDetail: CurrencyDetailDto = {
              currency,
              id: currencyId,
              purchaseAmount: 0.0,
              payout: 0.0,
            };
            for (const item of affiliateLink.purchaseActivities) {
              if (item.currencyId === currencyId) {
                currencyDetail.purchaseAmount = currencyDetail.purchaseAmount + item.purchaseAmount;
                currencyDetail.payout = currencyDetail.payout + item.commissionPayout;
              }
            }
            listCurrencyDetail.push(currencyDetail);
          });
          const affiliateSummary: AffiliateLinkSummaryDto = {
            accounts: affiliateLink.createAccountActivities.length,
            clicks: affiliateLink.clickActivities.length,
            currencyDetail: listCurrencyDetail,
            deliveryMethod: affiliateLink.deliveryMethod,
            deliveryMethodId: affiliateLink.deliveryMethodId,
            description: affiliateLink.description,
            id: affiliateLink.id,
            linkName: affiliateLink.linkName,
            payout: affiliateLink.payout,
            productId: affiliateLink.productId,
            purchases: affiliateLink.purchaseActivities.length,
            purchasesAmount: totalAmount,
            purchasesNumberWithoutPayment: affiliateLink.purchaseNumberWithoutPayout,
          };
          this.summaryData.push(affiliateSummary);
        }
        ));
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }

  // Jan-Mar = 0
  // Apr-Jun = 1
  // Jul-Sep = 2
  // Oct-Dec = 3
  getQuarter(date) {
    const date1 = date || new Date(); // If no date supplied, use today
    return Math.floor(date1.getMonth() / 3);
  }

  generateActivityCountsForQuarter(activities): number[] {
    const purchaseAmountsByQuarter: number[] = [0, 0, 0, 0];
    activities.forEach(element => {
      purchaseAmountsByQuarter[this.getQuarter(new Date(element.dateCreatedUtc))] += 1;
    });
    return purchaseAmountsByQuarter;
  }

  generatePurchaseAmountsForQuarter(activities): number[] {
    const activitiesCountByQuarter: number[] = [0, 0, 0, 0];
    activities.forEach(element => {
      activitiesCountByQuarter[this.getQuarter(new Date(element.dateCreatedUtc))] += element.purchaseAmount;
    });
    return activitiesCountByQuarter;
  }
}
